import { isTottusStandalone } from '@digital-retail/store-manager';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { withApplicationContext } from '../../utils/ApplicationContext';
import { withProduct } from '../../utils/ProductContext';
import Warranty from '../../utils/Warranty';
import { CartSection } from '../Cart/Cart';
import AddToCartError from '../ui/AddToCartError/AddToCartError';
import { Modal } from '../ui/Modal/Modal';
import ProductVariations from './ProductVariations/ProductVariations';

const ProductOverlay = ({
  onModalCloseHandler,
  labels,
  product,
  appCtx,
  isAddToCartInProgress,
  addProductToCart,
  cart,
  hasAddToCartError,
  isAddToCartSuccessful,
  show401Error,
  addToCartErrorCode,
}) => {
  let fullScreen = appCtx.deviceType !== 'desktop';
  if (hasAddToCartError && appCtx.deviceType !== 'desktop') {
    fullScreen = false;
  }

  const [hasCrossSelling, setCrossSelling] = useState(false);
  const [showWarrantyModal, setShowWarrantyModal] = useState(false);
  const [isWarrantyAdded, setIsWarrantyAdded] = useState(true);
  const [warrantyModalItem, setWarrantyModalItem] = useState(null);
  const [modalRoot, setModalRoot] = useState(null);

  const getBenefitsLabels = () => {
    return {
      WARRANTY_BENEFITS_LABEL:
        appCtx?.siteConfig?.textDictionary?.WARRANTY_BENEFITS_LABEL ?? 'Beneficios de extender la garantía',
      MORE_WARRANTY_BENEFITS: appCtx?.siteConfig?.textDictionary?.MORE_WARRANTY_BENEFITS ?? 'Más beneficios',
      RECOMMENDED_WARRANTY: appCtx?.siteConfig?.textDictionary?.RECOMMENDED_WARRANTY ?? '¡Recomendado!',
      PDP_NO_THANKS: appCtx?.siteConfig?.textDictionary?.PDP_NO_THANKS ?? 'Sin garantía extendida',
      WARRANTY_ADDED: appCtx?.siteConfig?.textDictionary?.WARRANTY_ADDED ?? 'Agregado',
      WARRANTY_LABEL_TEMPLATE: appCtx?.siteConfig?.textDictionary?.WARRANTY_LABEL_TEMPLATE ?? '{warrantyName}',
      EXTENDED_WARRANTY_TITLE: appCtx?.siteConfig?.textDictionary?.EXTENDED_WARRANTY_TITLE ?? 'Protege tu producto',
    };
  };

  useEffect(() => {
    const root = document.querySelector('.modal-container-desktop') || document.body;
    setModalRoot(root);
  }, []);

  useEffect(() => {
    if (!cart.showCart) {
      onModalCloseHandler();
    }
  }, [cart.showCart]);

  const { totalProductsCount } = cart;
  useEffect(() => {
    if (typeof totalProductsCount !== 'undefined') {
      appCtx.changeCartCount(totalProductsCount);
    }
  }, [totalProductsCount]);
  const gscCategoryId = product?.GSCCategoryId ?? product?.attributes?.GSCCategoryId ?? '';
  useEffect(() => {
    if (showWarrantyModal && !isWarrantyAdded) {
      const overlayElements = document.querySelectorAll('.doc-click-overlay');
      const modifiedOverlays = [];

      overlayElements.forEach((overlay) => {
        if (overlay && modalRoot && overlay !== modalRoot) {
          const originalDisplay = overlay.style.display || '';
          modifiedOverlays.push({
            element: overlay,
            originalDisplay,
          });
          // eslint-disable-next-line no-param-reassign
          overlay.style.display = 'none';
        }
      });
      return () => {
        modifiedOverlays.forEach(({ element, originalDisplay }) => {
          // eslint-disable-next-line no-param-reassign
          element.style.display = originalDisplay;
        });
      };
    }
    return undefined;
  }, [showWarrantyModal, isWarrantyAdded, modalRoot]);

  const getDesktopOptions = () => {
    let width;
    if (hasAddToCartError) {
      width = 500;
    } else {
      width = 720;
    }
    return {
      width,
      height: 'auto',
      overflow: 'hidden',
      disableOutsideClick: true,
      minHeight: hasAddToCartError ? 251 : 0,
    };
  };

  const getMobileOptions = () => {
    let width;
    let height;
    if (hasAddToCartError) {
      height = 264;
      width = 335;
    } else {
      height = '100vh';
      width = '100vw';
    }
    return {
      width,
      height,
      overflow: 'hidden',
      disableOutsideClick: true,
    };
  };

  const hasSizes =
    (product?.variants?.[1]?.options ?? []).length || (product?.variants?.[0]?.options?.[0]?.sizes ?? []).length;

  const handleShowWarrantyModal = (item) => {
    if (appCtx.deviceType === 'desktop' && item?.warrantyOptions?.fieldOptions?.length > 0) {
      if (gscCategoryId) {
        const enhancedItem = {
          ...item,
          attributes: {
            ...(item.attributes || {}),
            GSCCategoryId: gscCategoryId,
          },
        };
        setWarrantyModalItem(enhancedItem);
        setShowWarrantyModal(true);
        setIsWarrantyAdded(false);
      }
    } else {
      setIsWarrantyAdded(true);
    }
  };

  const handleWarrantySelection = (warrantyOption) => {
    setShowWarrantyModal(false);

    if (cart.pendingProductData) {
      if (warrantyOption) {
        cart.setWarrantyOptionCurrent({ ...warrantyOption, selected: true });
      }

      cart
        .continueAfterWarranty(warrantyOption)
        .then(() => {
          setIsWarrantyAdded(true);
        })
        .catch(() => {
          setIsWarrantyAdded(true);
        });
    } else if (warrantyModalItem) {
      if (warrantyOption) {
        const value = warrantyOption.variantId || warrantyOption.value || '';
        const warrantyOfferingId = warrantyOption?.offeringId ?? '';

        const { commerceItemId, skuId } = warrantyModalItem;

        cart.setWarrantyOptionCurrent({ ...warrantyOption, selected: true });
        cart
          .setWarrantyOption({
            appCtx,
            item: { commerceItemId, skuId: value },
            productContext: product,
            variantId: skuId,
            setWarrantyOptionCurrent: cart.setWarrantyOptionCurrent,
            cartLineId: warrantyModalItem.warrantyOptions.cartLineId || '',
            totalQuantity: warrantyModalItem.totalQuantity,
            offeringId: warrantyOfferingId,
          })
          .then(() => {
            setIsWarrantyAdded(true);
          })
          .catch(() => {
            setIsWarrantyAdded(true);
          });
      } else {
        setIsWarrantyAdded(true);
      }
    } else {
      setIsWarrantyAdded(true);
    }
  };
  const content = (actions) => {
    if (hasAddToCartError) {
      return (
        <AddToCartError
          labels={labels}
          onModalCloseHandler={onModalCloseHandler}
          show401Error={show401Error}
          setShowLoginForm={cart.setShowLoginForm}
          addToCartErrorCode={addToCartErrorCode}
        />
      );
    }
    if (isAddToCartSuccessful) {
      return (
        <CartSection
          setBusy={actions.setBusy}
          cart={cart}
          hasCrossSelling={hasCrossSelling}
          setCrossSelling={setCrossSelling}
          productOverlayContext={product}
          showWarrantyModal={handleShowWarrantyModal}
          isWarrantyAdded={isWarrantyAdded}
        />
      );
    }
    if (hasSizes) {
      return (
        <ProductVariations
          labels={labels}
          appCtx={appCtx}
          productOverlayContext={product}
          onModalCloseHandler={onModalCloseHandler}
          isAddToCartInProgress={isAddToCartInProgress}
          addProductToCart={addProductToCart}
        />
      );
    }
    return null;
  };
  if (isTottusStandalone(appCtx.store) && !hasAddToCartError) {
    return null;
  }

  return (
    <>
      {(hasAddToCartError || isAddToCartSuccessful || hasSizes) && (
        <Modal
          className={appCtx.deviceType === 'mobile' && hasAddToCartError ? 'error-modal' : null}
          withPadding={false}
          showCloseButton={false}
          content={content}
          toggleState={{ setVisible: cart.setShowCart, visible: cart.showCart }}
          fullScreen={fullScreen}
          options={{
            ...(appCtx.deviceType === 'desktop' ? getDesktopOptions() : getMobileOptions()),
          }}
          deviceType={appCtx.deviceType}
          stopScroll
        />
      )}
      {showWarrantyModal &&
        !isWarrantyAdded &&
        warrantyModalItem &&
        modalRoot &&
        appCtx.deviceType === 'desktop' &&
        ReactDOM.createPortal(
          <div className="warranty-modal-container">
            <Warranty
              cart={cart}
              appCtx={appCtx}
              product={product}
              item={warrantyModalItem}
              isVisible={showWarrantyModal}
              onClose={() => {
                setShowWarrantyModal(false);
                setIsWarrantyAdded(true);
              }}
              onWarrantySelected={handleWarrantySelection}
              benefitsLabels={getBenefitsLabels()}
            />
          </div>,
          modalRoot
        )}
    </>
  );
};

ProductOverlay.defaultProps = {
  onModalCloseHandler: () => {},
  labels: {},
  show401Error: false,
  addToCartErrorCode: '',
};

ProductOverlay.propTypes = {
  onModalCloseHandler: PropTypes.func,
  labels: PropTypes.object,
  product: PropTypes.object.isRequired,
  appCtx: PropTypes.object.isRequired,
  isAddToCartSuccessful: PropTypes.bool.isRequired,
  isAddToCartInProgress: PropTypes.bool.isRequired,
  addProductToCart: PropTypes.func.isRequired,
  cart: PropTypes.object.isRequired,
  hasAddToCartError: PropTypes.bool.isRequired,
  show401Error: PropTypes.bool,
  addToCartErrorCode: PropTypes.string,
};

export default withApplicationContext(withProduct(ProductOverlay));
